import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { membersAPI, hubError } from '../lib/api';
import { useLiveEvents } from '../context/LiveEventsContext';
import { TopBar } from '../components/layout/TopBar';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import { Users, MoreVertical, Check, X, Loader2, Eye, Search, UserMinus } from 'lucide-react';
import { toast } from 'sonner';
import MemberProfileDialog from './MemberProfileDialog';
import { Pagination } from '../components/Pagination';
import { usePagination } from '../hooks/usePagination';

const confirmCopy = {
  reject: {
    title: '¿Rechazar esta solicitud?',
    description: 'La persona no podrá ver el contenido de la iglesia en la app. Podrás aprobarla más adelante desde la pestaña "Rechazados".',
    action: 'Rechazar solicitud',
  },
  remove: {
    title: '¿Expulsar a este miembro?',
    description: 'Se eliminará su vínculo con la iglesia y perderá el acceso al contenido. Esta acción no se puede deshacer.',
    action: 'Expulsar miembro',
  },
};

const statusMap = {
  approved: { label: 'Aprobado', color: 'bg-green-100 text-green-700' },
  pending: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-700' },
  rejected: { label: 'Rechazado', color: 'bg-red-100 text-red-700' },
};

const LIVE_MEMBER_EVENTS = ['member.approved', 'member.rejected'];

export default function MembersPage() {
  const { selectedChurchId, canUpdate, canDelete } = useAuth();
  const { setMobileMenuOpen, churchInfo } = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [members, setMembers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const filter = searchParams.get('status') || 'all';
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [profileMemberId, setProfileMemberId] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [confirmAction, setConfirmAction] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const setFilter = (value) => setSearchParams(value === 'all' ? {} : { status: value });

  const refreshPendingCount = useCallback(() => {
    membersAPI.list(selectedChurchId, 'pending')
      .then(res => setPendingCount(res.data?.total ?? (res.data?.data || []).length))
      .catch(() => {});
  }, [selectedChurchId]);

  const pg = usePagination(`${filter}|${applied}|${selectedChurchId}`);

  const fetchMembers = useCallback(() => {
    setLoading(true);
    membersAPI.list(selectedChurchId, filter === 'all' ? undefined : filter, applied || undefined, pg.params)
      .then(res => {
        setMembers(res.data?.data || []);
        setTotal(res.data?.total || 0);
      })
      .catch(err => toast.error(hubError(err, 'Error al cargar miembros')))
      .finally(() => setLoading(false));
  }, [selectedChurchId, filter, applied, pg.params]);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);
  useEffect(() => { refreshPendingCount(); }, [refreshPendingCount]);

  useLiveEvents(LIVE_MEMBER_EVENTS, (events) => {
    const last = events[events.length - 1];
    const name = [last.payload?.firstname, last.payload?.lastname].filter(Boolean).join(' ') || 'Un miembro';
    toast.info(last.event === 'member.approved' ? `${name} fue aprobado` : `${name} fue rechazado`);
    fetchMembers();
    refreshPendingCount();
  });

  const runAction = async (memberId, action) => {
    setBusyId(memberId);
    try {
      if (action === 'approve') { await membersAPI.approve(memberId); toast.success('Miembro aprobado'); }
      else if (action === 'reject') { await membersAPI.reject(memberId); toast.success('Solicitud rechazada'); }
      else { await membersAPI.remove(memberId); toast.success('Miembro expulsado de la iglesia'); }
      fetchMembers();
      refreshPendingCount();
    } catch (err) { toast.error(hubError(err, 'Error al actualizar el miembro')); }
    finally { setBusyId(null); }
  };

  const askConfirm = (member, action) => setConfirmAction({ member, action });

  const confirmAndRun = async () => {
    const { member, action } = confirmAction;
    setConfirmAction(null);
    await runAction(member.id, action);
  };

  return (
    <div>
      <TopBar title="Miembros" onMenuClick={() => setMobileMenuOpen(true)} churchInfo={churchInfo} />
      <div className="px-4 sm:px-6 lg:px-8 py-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
          <Tabs value={filter} onValueChange={setFilter}>
            <TabsList>
              <TabsTrigger value="all" data-testid="members-filter-all">Todos</TabsTrigger>
              <TabsTrigger value="approved" data-testid="members-filter-approved">Aprobados</TabsTrigger>
              <TabsTrigger value="pending" data-testid="members-filter-pending">
                Solicitudes
                {pendingCount > 0 && (
                  <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#C58B2A] text-white text-[10px] font-semibold" data-testid="members-pending-badge">
                    {pendingCount}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="rejected" data-testid="members-filter-rejected">Rechazados</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="relative sm:ml-auto w-full sm:w-64">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && setApplied(search)}
              placeholder="Buscar por nombre o email"
              className="pl-9"
              data-testid="members-search-input"
            />
          </div>
        </div>

        <Pagination
          total={total} page={pg.page} pageSize={pg.pageSize}
          onPageChange={pg.setPage} onPageSizeChange={pg.setPageSize}
          label="miembros" testId="members-pagination"
        />

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 spinner text-[#056A84]" />
          </div>
        ) : members.length === 0 ? (
          <div className="empty-state">
            <Users className="w-12 h-12 text-muted-foreground/40" />
            <p>No hay miembros en esta categoría</p>
          </div>
        ) : (
          <>
            <div className="hidden md:block rounded-xl border bg-card overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">Nombre</th>
                    <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">Email</th>
                    <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">Rol</th>
                    <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">Estado</th>
                    <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => {
                    const status = m.church_approval_status || 'pending';
                    const statusInfo = statusMap[status] || statusMap.pending;
                    return (
                      <tr key={m.id} className="border-b table-row-hover">
                        <td className="p-3">
                          <button className="flex items-center gap-3 text-left hover:opacity-80" onClick={() => setProfileMemberId(m.id)} data-testid={`member-row-open-${m.id}`}>
                            {m.avatar_url ? (
                              <img src={m.avatar_url} alt={m.firstname} className="w-9 h-9 rounded-full object-cover" />
                            ) : (
                              <div className="w-9 h-9 rounded-full bg-[#00CCFF]/10 flex items-center justify-center text-sm font-medium text-[#056A84]">
                                {(m.firstname || 'U')[0]}{(m.lastname || '')[0]}
                              </div>
                            )}
                            <span>
                              <span className="block text-sm font-medium text-[#056A84] hover:underline">{m.firstname || ''} {m.lastname || ''}</span>
                              <span className="block text-xs text-muted-foreground">{m.created_at ? `Desde ${new Date(m.created_at).toLocaleDateString('es-ES')}` : ''}</span>
                            </span>
                          </button>
                        </td>
                        <td className="p-3 text-sm">{typeof m.email === 'object' ? JSON.stringify(m.email) : m.email}</td>
                        <td className="p-3 text-sm capitalize">{typeof m.role === 'object' ? (m.role?.name || 'member') : (m.role || 'member')}</td>
                        <td className="p-3">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                            {statusInfo.label}
                          </span>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            {canUpdate('members') && status === 'pending' && (
                              <>
                                <Button
                                  size="sm"
                                  disabled={busyId === m.id}
                                  className="h-8 bg-green-600 hover:bg-green-700 text-white text-xs"
                                  onClick={() => runAction(m.id, 'approve')}
                                  data-testid={`members-approve-inline-${m.id}`}
                                >
                                  {busyId === m.id ? <Loader2 className="w-3 h-3 spinner" /> : <><Check className="w-3 h-3 mr-1" /> Aprobar</>}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={busyId === m.id}
                                  className="h-8 text-xs text-destructive border-destructive/30 hover:bg-destructive/5"
                                  onClick={() => askConfirm(m, 'reject')}
                                  data-testid={`members-reject-inline-${m.id}`}
                                >
                                  <X className="w-3 h-3 mr-1" /> Rechazar
                                </Button>
                              </>
                            )}
                            <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" data-testid={`members-actions-${m.id}`}>
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => setProfileMemberId(m.id)} data-testid={`members-view-${m.id}`}>
                                <Eye className="w-4 h-4 mr-2 text-[#056A84]" /> Ver perfil
                              </DropdownMenuItem>
                              {canUpdate('members') && status !== 'approved' && (
                                <DropdownMenuItem onClick={() => runAction(m.id, 'approve')} data-testid={`members-approve-${m.id}`}>
                                  <Check className="w-4 h-4 mr-2 text-green-600" /> Aprobar
                                </DropdownMenuItem>
                              )}
                              {canUpdate('members') && status !== 'rejected' && (
                                <DropdownMenuItem onClick={() => askConfirm(m, 'reject')} data-testid={`members-reject-${m.id}`}>
                                  <X className="w-4 h-4 mr-2 text-red-600" /> Rechazar
                                </DropdownMenuItem>
                              )}
                              {canDelete('members') && (
                                <DropdownMenuItem onClick={() => askConfirm(m, 'remove')} data-testid={`members-remove-${m.id}`}>
                                  <UserMinus className="w-4 h-4 mr-2 text-destructive" /> Expulsar
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="md:hidden space-y-3">
              {members.map((m) => {
                const status = m.church_approval_status || 'pending';
                const statusInfo = statusMap[status] || statusMap.pending;
                return (
                  <Card key={m.id}>
                    <CardContent className="p-4">
                      <button className="flex items-start justify-between w-full text-left" onClick={() => setProfileMemberId(m.id)} data-testid={`member-card-open-${m.id}`}>
                        <span className="flex items-center gap-3">
                          <span className="w-10 h-10 rounded-full bg-[#00CCFF]/10 flex items-center justify-center text-sm font-medium text-[#056A84]">
                            {(m.firstname || 'U')[0]}{(m.lastname || '')[0]}
                          </span>
                          <span>
                            <span className="block text-sm font-medium text-[#056A84]">{m.firstname || ''} {m.lastname || ''}</span>
                            <span className="block text-xs text-muted-foreground">{m.email}</span>
                          </span>
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                      </button>
                      {canUpdate('members') && status === 'pending' && (
                        <div className="flex gap-2 mt-3">
                          <Button size="sm" className="flex-1 bg-green-600 hover:bg-green-700 text-white text-xs" onClick={() => runAction(m.id, 'approve')} data-testid={`members-approve-mobile-${m.id}`}>
                            <Check className="w-3 h-3 mr-1" /> Aprobar
                          </Button>
                          <Button size="sm" variant="destructive" className="flex-1 text-xs" onClick={() => askConfirm(m, 'reject')} data-testid={`members-reject-mobile-${m.id}`}>
                            <X className="w-3 h-3 mr-1" /> Rechazar
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </>
        )}
      </div>

      <MemberProfileDialog
        memberId={profileMemberId}
        open={!!profileMemberId}
        onClose={() => setProfileMemberId(null)}
      />

      <AlertDialog open={!!confirmAction} onOpenChange={(open) => !open && setConfirmAction(null)}>
        <AlertDialogContent data-testid="members-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmAction ? confirmCopy[confirmAction.action].title : ''}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction && (
                <>
                  <span className="font-medium text-foreground">
                    {[confirmAction.member.firstname, confirmAction.member.lastname].filter(Boolean).join(' ') || confirmAction.member.email}
                  </span>
                  {' — '}
                  {confirmCopy[confirmAction.action].description}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="members-confirm-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
              onClick={confirmAndRun}
              data-testid="members-confirm-accept"
            >
              {confirmAction ? confirmCopy[confirmAction.action].action : ''}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
